import {
  ListChecks, MapPin, Clock, Package, User, Printer, CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type {
  Requisition, RequisitionItem 
} from '../../types';

interface PickListCardProps {
  readonly requisition: Requisition;
  readonly pickedItems: Set<string>;
  readonly onToggleItem: (lineId: string) => void;
  readonly onPrint: () => void;
  readonly onMarkComplete: () => void;
}

export function PickListCard({
  requisition,
  pickedItems,
  onToggleItem,
  onPrint,
  onMarkComplete,
}: PickListCardProps) {
  const neededByDate = new Date(requisition.neededBy);
  const isUrgent = neededByDate <= new Date(Date.now() + 24 * 60 * 60 * 1000);
  const allPicked = requisition.items.every((item) => pickedItems.has(item.lineId));
  const pickedCount = requisition.items.filter((item) => pickedItems.has(item.lineId)).length;

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              'rounded-md p-2',
              allPicked ? 'bg-green-100' : 'bg-blue-100'
            )}>
              {allPicked ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <ListChecks className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{requisition.requisitionId}</h3>
              <p className="flex items-center gap-1 text-sm text-gray-500">
                <MapPin className="h-3 w-3" /> {requisition.venueName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn(
              'rounded-full px-2 py-1 text-xs font-medium',
              allPicked
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
            )}>
              {pickedCount}/{requisition.items.length} picked
            </span>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <User className="h-4 w-4" /> {requisition.requestedByName}
          </span>
          <span className="flex items-center gap-1">
            <Package className="h-4 w-4" /> {requisition.totalItems} items
          </span>
          <span className={cn(
            'flex items-center gap-1',
            isUrgent && 'font-medium text-red-600'
          )}>
            <Clock className="h-4 w-4" />
            {isUrgent ? 'Urgent' : `Need by ${neededByDate.toLocaleDateString()}`}
          </span>
        </div>
      </div>

      {/* Items List */}
      <div className="divide-y">
        {requisition.items.map((item) => (
          <PickListItem
            key={item.lineId}
            item={item}
            isPicked={pickedItems.has(item.lineId)}
            onToggle={() => onToggleItem(item.lineId)}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between border-t bg-gray-50 p-3">
        <button
          type="button"
          onClick={onPrint}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          <Printer className="h-4 w-4" />
          Print List
        </button>
        <button
          type="button"
          onClick={onMarkComplete}
          disabled={!allPicked}
          className={cn(
            'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium',
            allPicked
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'cursor-not-allowed bg-gray-200 text-gray-400'
          )}
        >
          <CheckCircle2 className="h-4 w-4" />
          Mark as Fulfilled
        </button>
      </div>
    </div>
  );
}

interface PickListItemProps {
  readonly item: RequisitionItem;
  readonly isPicked: boolean;
  readonly onToggle: () => void;
}

function PickListItem({
  item, isPicked, onToggle 
}: PickListItemProps) {
  const quantity = item.approvedQuantity ?? item.requestedQuantity;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-3 p-3 text-left transition-colors',
        isPicked ? 'bg-green-50' : 'hover:bg-gray-50'
      )}
    >
      <div className={cn(
        'flex h-5 w-5 items-center justify-center rounded border-2',
        isPicked
          ? 'border-green-600 bg-green-600'
          : 'border-gray-300 bg-white'
      )}>
        {isPicked && (
          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <div className="flex-1">
        <p className={cn(
          'font-medium',
          isPicked ? 'text-gray-500 line-through' : 'text-gray-900'
        )}>
          {item.itemName}
        </p>
        <p className="text-xs text-gray-500">{item.sku}</p>
      </div>
      <div className="text-right">
        <p className={cn(
          'font-semibold',
          isPicked ? 'text-gray-400' : 'text-gray-900'
        )}>
          {quantity} {item.unit}
        </p>
        {item.notes && (
          <p className="text-xs text-gray-500">{item.notes}</p>
        )}
      </div>
    </button>
  );
}
